import { memo, useRef, useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';

import { CloseCircleFilled } from '@ant-design/icons';
import { cloneDeep, isEqual } from 'lodash-es';

import FilterConditionItem from '../FilterConditionItem';

import Colon from '@/components/Colon';
import SvgIcon from '@/components/SvgIcon';

import './style.less';

const getLetterList = (arr) => {
  const result = [];
  (arr || []).forEach((item) => {
    if (item.firstLetter && !result.includes(item.firstLetter)) {
      result.push(item.firstLetter);
    }
  });
  return result.sort();
};

const formatMoreOtherData = (moreOtherData) => {
  if (!moreOtherData) {
    return [];
  }
  const { paramNameMap, paramGroupMap } = moreOtherData;
  const result = [];
  Object.keys(paramGroupMap).forEach((key) => {
    result.push({
      name: paramNameMap[key],
      list: paramGroupMap[key].map((item) => {
        return {
          ...item,
          parameterCode: key,
        };
      }),
      value: 'name',
      label: 'name',
      parameterCode: key,
      showInputCondition: true,
    });
  });
  return result.filter((item) => item?.list?.length > 0);
};

const formatMoreOtherHas = (queryParameterValue) => {
  if (!queryParameterValue) return {};
  const moreOtherHasState = {};
  queryParameterValue.split('$').forEach((item) => {
    const tex = item.split('@@@@@')[0];
    const parameterCode = item.split('@@@@@')[1];
    if (!moreOtherHasState[parameterCode]) {
      moreOtherHasState[parameterCode] = [];
    }
    moreOtherHasState[parameterCode].push({
      name: tex,
      parameterCode,
    });
  });
  return moreOtherHasState;
};

const Index = forwardRef((props, ref) => {
  const catalogRef = useRef(null);
  const brandRef = useRef(null);
  const packageRef = useRef(null);
  const bzRef = useRef(null);
  const bqRef = useRef(null);
  const rzRef = useRef(null);
  const moreOtherRefs = useRef([]);
  const [moreOtherHas, setMoreOtherHas] = useState(formatMoreOtherHas(props?.sendSearchParams.queryParameterValue));
  const [popoverKey, setPopoverKey] = useState('');
  const [top, setTop] = useState(21);
  const isComposing = useRef(false); // 标记是否正在输入中文
  const [showPopoverType, setShowPopoverType] = useState(''); // 悬浮窗出现的方式 分两种 click  hover

  const [isRzRedDotDisplayed, setIsRzRedDotDisplayed] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('isRzRedDotDisplayed');
    if (stored === 'true') {
      setIsRzRedDotDisplayed(true);
    }
  }, []);

  const [catalogData, setCatalogData] = useState({
    name: '类目',
    value: 'value',
    label: 'label',
    list: props?.catalogFilters,
    letterList: getLetterList(props?.catalogFilters),
    showInputCondition: false,
  });

  const [brandData, setBrandData] = useState({
    name: '品牌',
    value: 'value',
    label: 'label',
    list: props?.brandFilters,
    letterList: getLetterList(props?.brandFilters),
    showInputCondition: false,
  });

  const [packageData, setPackageData] = useState({
    name: '封装',
    value: 'label',
    label: 'label',
    list: props?.encapFilters,
    showLetterCondition: false,
    showInputCondition: true,
  });

  const [bzData, setBzData] = useState({
    name: '包装',
    value: 'label',
    label: 'label',
    list: props?.arrangeFilters,
    showLetterCondition: false,
    showInputCondition: true,
  });

  const [bqData, setBqData] = useState({
    name: '标签',
    value: 'label',
    label: 'label',
    list: props?.productTagFilters,
    showLetterCondition: false,
    showInputCondition: true,
  });

  const [rzData, setRzData] = useState({
    name: '认证信息',
    value: 'label',
    label: 'label',
    list: props?.authenticationFilters,
    showLetterCondition: false,
    showInputCondition: true,
  });

  const propsMoreOtherData = formatMoreOtherData(props?.moreOtherData);
  const [moreOtherData, setMoreOtherData] = useState(propsMoreOtherData);

  useEffect(() => {
    if (!isEqual(catalogData?.list, props?.catalogFilters)) {
      setCatalogData({
        ...catalogData,
        list: props?.catalogFilters,
        letterList: getLetterList(props?.catalogFilters),
      });
    }
  }, [catalogData, props?.catalogFilters]);

  useEffect(() => {
    if (!isEqual(brandData?.list, props?.brandFilters)) {
      setBrandData({
        ...brandData,
        list: props?.brandFilters,
        letterList: getLetterList(props?.brandFilters),
      });
    }
  }, [brandData, props?.brandFilters]);

  useEffect(() => {
    if (!isEqual(packageData?.list, props?.encapFilters)) {
      setPackageData({
        ...packageData,
        list: props?.encapFilters,
      });
    }
  }, [packageData, props?.encapFilters]);

  useEffect(() => {
    if (!isEqual(bzData?.list, props?.arrangeFilters)) {
      setBzData({
        ...bzData,
        list: props?.arrangeFilters,
      });
    }
  }, [bzData, props?.arrangeFilters]);

  useEffect(() => {
    if (!isEqual(bqData?.list, props?.productTagFilters)) {
      setBqData({
        ...bqData,
        list: props?.productTagFilters,
      });
    }
  }, [bqData, props?.productTagFilters]);

  useEffect(() => {
    if (!isEqual(rzData?.list, props?.authenticationFilters)) {
      setRzData({
        ...rzData,
        list: props?.authenticationFilters,
      });
    }
  }, [rzData, props?.authenticationFilters]);

  useEffect(() => {
    const newMoreOtherData = formatMoreOtherData(props?.moreOtherData);
    if (!isEqual(moreOtherData, newMoreOtherData)) {
      setMoreOtherData(newMoreOtherData);
    }
  }, [moreOtherData, props?.moreOtherData]);

  useEffect(() => {
    const newMoreOtherHas = formatMoreOtherHas(props?.sendSearchParams.queryParameterValue);
    if (!isEqual(newMoreOtherHas, moreOtherHas)) {
      setMoreOtherHas(newMoreOtherHas);
    }
  }, [moreOtherHas, props?.sendSearchParams.queryParameterValue]);

  const onlyOpenOneShowMore = (name, value) => {
    const catalogFn = () => {
      if (value) {
        brandRef?.current?.resetInitStatus();
        packageRef?.current?.resetInitStatus();
        bzRef?.current?.resetInitStatus();
        bqRef?.current?.resetInitStatus();
        rzRef?.current?.resetInitStatus();
      }
    };
    const brandFn = () => {
      if (value) {
        catalogRef?.current?.resetInitStatus();
        packageRef?.current?.resetInitStatus();
        bzRef?.current?.resetInitStatus();
        bqRef?.current?.resetInitStatus();
        rzRef?.current?.resetInitStatus();
      }
    };
    const packageFn = () => {
      if (value) {
        catalogRef?.current?.resetInitStatus();
        brandRef?.current?.resetInitStatus();
        bzRef?.current?.resetInitStatus();
        bqRef?.current?.resetInitStatus();
        rzRef?.current?.resetInitStatus();
      }
    };
    const bzFn = () => {
      if (value) {
        catalogRef?.current?.resetInitStatus();
        brandRef?.current?.resetInitStatus();
        packageRef?.current?.resetInitStatus();
        bqRef?.current?.resetInitStatus();
        rzRef?.current?.resetInitStatus();
      }
    };
    const bqFn = () => {
      if (value) {
        catalogRef?.current?.resetInitStatus();
        brandRef?.current?.resetInitStatus();
        packageRef?.current?.resetInitStatus();
        bzRef?.current?.resetInitStatus();
        rzRef?.current?.resetInitStatus();
      }
    };
    const rzFn = () => {
      if (value) {
        catalogRef?.current?.resetInitStatus();
        brandRef?.current?.resetInitStatus();
        packageRef?.current?.resetInitStatus();
        bzRef?.current?.resetInitStatus();
        bqRef?.current?.resetInitStatus();
      }
    };
    const fnMap = {
      类目: catalogFn,
      品牌: brandFn,
      封装: packageFn,
      包装: bzFn,
      标签: bqFn,
      认证信息: rzFn,
    };
    typeof fnMap[name] === 'function' && fnMap[name]();
  };

  const moreOtherOnlyOpenOne = (name, value) => {
    if (value) {
      const index = moreOtherData.findIndex((item) => item.parameterCode === name);
      for (let i = 0; i < moreOtherData.length; i++) {
        if (i !== index) {
          moreOtherRefs?.current[i]?.resetInitStatus();
        }
      }
    }
  };

  const changeSendParams = (selectDataList, name) => {
    const catalogFn = () => {
      const updateParams = {
        catalogIdFilter: selectDataList.map((item) => item.value).join('$'),
        currentPage: 1,
      };
      if ((selectDataList.length === 0 && props?.catalogFiltersLen > 1) || selectDataList.length > 1) {
        Object.assign(updateParams, {
          queryParameterValue: '',
          lastParamName: '',
        });
        setMoreOtherHas({});
      }
      props?.changeSendSearchParams(updateParams);
    };
    const brandFn = () => {
      props?.changeSendSearchParams({
        brandIdFilter: selectDataList.map((item) => item.value).join('$'),
        currentPage: 1,
      });
    };
    const packageFn = () => {
      props?.changeSendSearchParams({
        standardFilter: selectDataList.map((item) => item.label).join('$'),
        currentPage: 1,
      });
    };
    const bzFn = () => {
      props?.changeSendSearchParams({
        arrangeFilter: selectDataList.map((item) => item.label).join('$'),
        currentPage: 1,
      });
    };
    const bqFn = () => {
      props?.changeSendSearchParams({
        labelFilter: selectDataList.map((item) => item.label).join('$'),
        currentPage: 1,
      });
    };
    const rzFn = () => {
      props?.changeSendSearchParams({
        authenticationFilter: selectDataList.map((item) => item.label).join('$'),
        currentPage: 1,
      });
    };
    const fnMap = {
      类目: catalogFn,
      品牌: brandFn,
      封装: packageFn,
      包装: bzFn,
      标签: bqFn,
      认证信息: rzFn,
    };
    typeof fnMap[name] === 'function' && fnMap[name]();
    setPopoverKey('');
  };

  const moreOtherChangeSendParams = (selectDataList, name) => {
    const queryParameterValueArr = [];
    selectDataList.forEach((item) => {
      const tex = item.name;
      const keyName = tex + '@@@@@' + item.parameterCode; // 以前逻辑是单个@符号 但是单个可能tex中也含有@ 导致出现bug 特做处理
      queryParameterValueArr.push(keyName);
    });
    const queryParameterValueState = props?.sendSearchParams.queryParameterValue
      ? props?.sendSearchParams.queryParameterValue.split('$').filter((row) => row.split('@@@@@')[1] !== name)
      : [];
    props?.changeSendSearchParams({
      queryParameterValue: [...queryParameterValueState, ...queryParameterValueArr].join('$'),
      lastParamName: name,
      currentPage: 1,
    });
    const moreOtherHasState = cloneDeep(moreOtherHas);
    moreOtherHasState[name] = selectDataList;
    setMoreOtherHas(moreOtherHasState);
    setPopoverKey('');
  };

  const deleteList = (name) => {
    if (name === '包装') {
      props?.changeSendSearchParams({
        arrangeFilter: '',
        currentPage: 1,
      });
    } else if (name === '标签') {
      props?.changeSendSearchParams({
        labelFilter: '',
        currentPage: 1,
      });
    } else if (name === '认证信息') {
      props?.changeSendSearchParams({
        authenticationFilter: '',
        currentPage: 1,
      });
    }
  };

  const moreOtherDeleteList = (item) => {
    const { parameterCode } = item;
    const queryParameterValueState = props?.sendSearchParams.queryParameterValue
      ? props?.sendSearchParams.queryParameterValue.split('$')
      : [];
    props?.changeSendSearchParams({
      queryParameterValue: queryParameterValueState.filter((row) => row.split('@@@@@')[1] !== parameterCode).join('$'),
      lastParamName: parameterCode,
      currentPage: 1,
    });
    const moreOtherHasState = cloneDeep(moreOtherHas);
    moreOtherHasState[parameterCode] = [];
    setMoreOtherHas(moreOtherHasState);
  };

  const referenceRef = useRef(null);
  const changePopoverKey = (e, name) => {
    const refTop = !isNaN(referenceRef?.current?.getBoundingClientRect()?.top)
      ? referenceRef?.current?.getBoundingClientRect()?.top
      : 0;
    const domCls =
      name === '包装'
        ? '.more-item-bz'
        : name === '标签'
          ? '.more-item-bq'
          : name === '认证信息'
            ? '.more-item-rz'
            : '.more-item-' + name;
    const hoverTop = !isNaN(document.querySelector(domCls)?.getBoundingClientRect()?.top)
      ? document.querySelector(domCls)?.getBoundingClientRect()?.top
      : 0;
    let topState;
    if (hoverTop - refTop <= 22) {
      topState = 31;
    } else {
      topState = hoverTop - refTop + 31;
    }
    setTop(topState);
    setPopoverKey(name);
  };

  const changeComposition = (value) => {
    isComposing.current = value;
  };

  const resetAllCondition = () => {
    props?.changeSendSearchParams({
      catalogIdFilter: '',
      brandIdFilter: '',
      standardFilter: '',
      arrangeFilter: '',
      labelFilter: '',
      authenticationFilter: '',
      queryParameterValue: '',
      lastParamName: '',
      currentPage: 1,
    });
  };

  const bzPopoverContent = () => {
    return (
      <div className={'_lg:w-[1310px] w-[1170px] px-[11px] text-[12px]'}>
        <FilterConditionItem
          data={bzData}
          ref={bzRef}
          onlyOpenOneShowMore={onlyOpenOneShowMore}
          changeSendParams={changeSendParams}
          selectValue={props?.sendSearchParams.arrangeFilter}
          changeComposition={changeComposition}
        />
      </div>
    );
  };

  const bqPopoverContent = () => {
    return (
      <div className={'_lg:w-[1310px] w-[1170px] px-[11px] text-[12px]'}>
        <FilterConditionItem
          data={bqData}
          ref={bqRef}
          onlyOpenOneShowMore={onlyOpenOneShowMore}
          changeSendParams={changeSendParams}
          selectValue={props?.sendSearchParams.labelFilter}
          changeComposition={changeComposition}
        />
      </div>
    );
  };

  const rzPopoverContent = () => {
    return (
      <div className={'_lg:w-[1310px] w-[1170px] px-[11px] text-[12px]'}>
        <FilterConditionItem
          data={rzData}
          ref={rzRef}
          onlyOpenOneShowMore={onlyOpenOneShowMore}
          changeSendParams={changeSendParams}
          selectValue={props?.sendSearchParams.authenticationFilter}
          changeComposition={changeComposition}
        />
      </div>
    );
  };

  const renderMoreOther = () => {
    const item = moreOtherData.find((row) => row.parameterCode === popoverKey);
    const index = moreOtherData.findIndex((row) => row.parameterCode === popoverKey);
    return item ? (
      <div className={'_lg:w-[1310px] w-[1170px] px-[11px] text-[12px]'}>
        <FilterConditionItem
          data={item}
          ref={(el) => (moreOtherRefs.current[index] = el)}
          onlyOpenOneShowMore={moreOtherOnlyOpenOne}
          changeSendParams={moreOtherChangeSendParams}
          selectValue={moreOtherHas}
          changeComposition={changeComposition}
        />
      </div>
    ) : (
      <></>
    );
  };

  // 只有在目录长度为1 或者选择的目录长度为1的情况下 才显示
  const showMoreOtherData =
    props?.catalogFilters.length === 1 || props?.sendSearchParams.catalogIdFilter.split('$').length === 1;

  const bzMouseEnter = (e) => {
    if (props?.sendSearchParams.arrangeFilter) {
      setShowPopoverType('hover');
      changePopoverKey(e, '包装');
    }
  };

  const bzClick = (e) => {
    e.stopPropagation();
    if (!props?.sendSearchParams.arrangeFilter) {
      if (popoverKey === '包装') {
        closePopover();
      } else {
        setShowPopoverType('click');
        changePopoverKey(e, '包装');
      }
    }
  };

  const bqMouseEnter = (e) => {
    if (props?.sendSearchParams.labelFilter) {
      setShowPopoverType('hover');
      changePopoverKey(e, '标签');
    }
  };

  const bqClick = (e) => {
    e.stopPropagation();
    if (!props?.sendSearchParams.labelFilter) {
      if (popoverKey === '标签') {
        closePopover();
      } else {
        setShowPopoverType('click');
        changePopoverKey(e, '标签');
      }
    }
  };

  const rzMouseEnter = (e) => {
    if (props?.sendSearchParams.authenticationFilter) {
      setShowPopoverType('hover');
      changePopoverKey(e, '认证信息');
    }
  };

  const rzClick = (e) => {
    e.stopPropagation();
    if (!props?.sendSearchParams.authenticationFilter) {
      if (popoverKey === '认证信息') {
        closePopover();
      } else {
        setShowPopoverType('click');
        changePopoverKey(e, '认证信息');
      }
    }

    // 点击后不再显示认证信息红点
    setIsRzRedDotDisplayed(() => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('isRzRedDotDisplayed', true);
      }
      return true;
    });
  };

  const moreOtherMouseEnter = (e, item) => {
    if (moreOtherHas?.[item.parameterCode]?.length > 0) {
      setShowPopoverType('hover');
      changePopoverKey(e, item.parameterCode);
    }
  };

  const moreOtherClick = (e, item) => {
    e.stopPropagation();
    if (!moreOtherHas?.[item.parameterCode]?.length) {
      if (popoverKey === item.parameterCode) {
        closePopover();
      } else {
        setShowPopoverType('click');
        changePopoverKey(e, item.parameterCode);
      }
    }
  };

  const closePopover = () => {
    if (!isComposing.current) {
      setPopoverKey('');
      setShowPopoverType('');
    }
  };

  useImperativeHandle(ref, () => ({
    resetAllCondition,
    closePopover,
  }));

  const [isBzHoverStatus, setIsBzHoverStatus] = useState(false);
  const BzArrowIcon = useMemo(() => {
    if (popoverKey === '包装' || isBzHoverStatus === true) {
      return 'so-arrow-active';
    } else {
      return 'so-arrow';
    }
  }, [isBzHoverStatus, popoverKey]);

  const [isBqHoverStatus, setIsBqHoverStatus] = useState(false);
  const BqArrowIcon = useMemo(() => {
    if (popoverKey === '标签' || isBqHoverStatus === true) {
      return 'so-arrow-active';
    } else {
      return 'so-arrow';
    }
  }, [isBqHoverStatus, popoverKey]);

  const [isRzHoverStatus, setIsRzHoverStatus] = useState(false);
  const RzArrowIcon = useMemo(() => {
    if (popoverKey === '认证信息' || isRzHoverStatus === true) {
      return 'so-arrow-active';
    } else {
      return 'so-arrow';
    }
  }, [isRzHoverStatus, popoverKey]);

  return (
    <ul className={'_lg:w-[1340px] m-auto w-[1200px] rounded-[10px]  bg-white px-[20px] pb-[7px] pt-[17px]'}>
      <li className={'border-b border-solid border-[#EFEFEF]'}>
        <FilterConditionItem
          data={catalogData}
          ref={catalogRef}
          onlyOpenOneShowMore={onlyOpenOneShowMore}
          changeSendParams={changeSendParams}
          selectValue={props?.sendSearchParams?.catalogIdFilter}
        />
      </li>
      <li className={'border-b border-solid border-[#EFEFEF] pt-[10px]'}>
        <FilterConditionItem
          data={brandData}
          ref={brandRef}
          onlyOpenOneShowMore={onlyOpenOneShowMore}
          changeSendParams={changeSendParams}
          selectValue={props?.sendSearchParams?.brandIdFilter}
        />
      </li>
      <li className={'border-b border-solid border-[#EFEFEF] pt-[10px]'}>
        <FilterConditionItem
          data={packageData}
          ref={packageRef}
          onlyOpenOneShowMore={onlyOpenOneShowMore}
          changeSendParams={changeSendParams}
          selectValue={props?.sendSearchParams?.standardFilter}
        />
      </li>
      <li
        className={'relative flex pt-[10px]'}
        onMouseLeave={() => {
          if (showPopoverType === 'hover') {
            closePopover();
          }
        }}
      >
        <div
          className={'mb-[10px] flex h-[22px] items-center justify-center text-[12px] text-[#69788A] mr-[6px]'}
          ref={referenceRef}
        >
          更多
          <Colon />
        </div>
        <ul className={'flex flex-1 flex-wrap items-center'}>
          {bzData?.list?.length > 0 && (
            <li
              className={`more-item-bz relative mb-[10px] mr-[10px] flex h-[22px] cursor-pointer flex-col items-center justify-center  rounded-[3px] border border-solid border-[#D9D9D9] bg-white hover:text-[#0093E6]
            ${popoverKey === '包装' ? 'rounded-bl-none rounded-br-none border-b-[#FFFFFF] text-[#0093E6]' : ''}`}
              onMouseEnter={(e) => {
                setIsBzHoverStatus(true);
                bzMouseEnter(e);
              }}
              onMouseLeave={() => setIsBzHoverStatus(false)}
              onClick={(e) => bzClick(e)}
            >
              <div className={'flex items-center px-[6px]'}>
                <div className={`pr-[3px] ${props?.sendSearchParams.arrangeFilter ? 'text-[#0093E6]' : ''}`}>包装</div>
                {!props?.sendSearchParams.arrangeFilter ? (
                  <SvgIcon
                    app="so"
                    iconName={BzArrowIcon}
                    className={`h-[6px] w-[9px] ${popoverKey === '包装' ? 'rotate-180 transform ' : ''}`}
                  />
                ) : (
                  <CloseCircleFilled className={'text-[#0093E6]'} onClick={() => deleteList('包装')} />
                )}
                {popoverKey === '包装' && (
                  <div className={'absolute bottom-[-1px] left-0 z-[12] h-[2px] w-full bg-white'}></div>
                )}
              </div>
            </li>
          )}
          {bqData?.list?.length > 0 && (
            <li
              className={`more-item-bq relative mb-[10px] mr-[10px] flex h-[22px] cursor-pointer flex-col items-center justify-center rounded-[3px] border border-solid border-[#D9D9D9] bg-white  hover:text-[#0093E6]
            ${popoverKey === '标签' ? 'rounded-bl-none rounded-br-none border-b-[#ffffff] text-[#0093E6]' : ''}`}
              onMouseEnter={(e) => {
                setIsBqHoverStatus(true);
                bqMouseEnter(e);
              }}
              onMouseLeave={() => setIsBqHoverStatus(false)}
              onClick={(e) => bqClick(e)}
            >
              <div className={'flex items-center px-[6px]'}>
                <div className={`pr-[3px] ${props?.sendSearchParams.labelFilter ? 'text-[#0093E6]' : ''}`}>标签</div>
                {!props?.sendSearchParams.labelFilter ? (
                  <SvgIcon
                    app="so"
                    iconName={BqArrowIcon}
                    className={`h-[6px] w-[9px] ${popoverKey === '标签' ? 'rotate-180 transform ' : ''}`}
                  />
                ) : (
                  <CloseCircleFilled className={'text-[#0093E6]'} onClick={() => deleteList('标签')} />
                )}
                {popoverKey === '标签' && (
                  <div className={'absolute bottom-[-1px] left-0 z-[12] h-[2px] w-full bg-white'}></div>
                )}
              </div>
            </li>
          )}
          {rzData?.list?.length > 0 && (
            <li
              className={`more-item-rz relative mb-[10px] mr-[10px] flex h-[22px] cursor-pointer flex-col items-center justify-center rounded-[3px] border border-solid border-[#D9D9D9] bg-white  hover:text-[#0093E6]
            ${popoverKey === '认证信息' ? 'rounded-bl-none rounded-br-none border-b-[#ffffff] text-[#0093E6]' : ''}`}
              onMouseEnter={(e) => {
                setIsRzHoverStatus(true);
                rzMouseEnter(e);
              }}
              onMouseLeave={() => setIsRzHoverStatus(false)}
              onClick={(e) => rzClick(e)}
            >
              <div className={'flex items-center px-[6px]'}>
                {/* 红点 */}
                {!isRzRedDotDisplayed && (
                  <div className="absolute right-[-3px] top-[-3px] w-[6px] h-[6px] rounded bg-[#EC5B56]"></div>
                )}
                <div className={`pr-[3px] ${props?.sendSearchParams.authenticationFilter ? 'text-[#0093E6]' : ''}`}>
                  认证信息
                </div>
                {!props?.sendSearchParams.authenticationFilter ? (
                  <SvgIcon
                    app="so"
                    iconName={RzArrowIcon}
                    className={`h-[6px] w-[9px] ${popoverKey === '认证信息' ? 'rotate-180 transform ' : ''}`}
                  />
                ) : (
                  <CloseCircleFilled className={'text-[#0093E6]'} onClick={() => deleteList('认证信息')} />
                )}
                {popoverKey === '认证信息' && (
                  <div className={'absolute bottom-[-1px] left-0 z-[12] h-[2px] w-full bg-white'}></div>
                )}
              </div>
            </li>
          )}
          {showMoreOtherData &&
            (moreOtherData || []).map((item, index) => {
              return (
                <li
                  className={`more-item-${item.parameterCode} relative mb-[10px] mr-[10px] flex h-[22px] cursor-pointer flex-col  items-center justify-center rounded-[3px] border border-solid border-[#D9D9D9] bg-white hover:text-[#0093E6]
            ${popoverKey === item.parameterCode ? 'rounded-bl-none rounded-br-none border-b-[#ffffff] text-[#0093E6]' : ''}`}
                  onMouseEnter={(e) => {
                    moreOtherMouseEnter(e, item);
                  }}
                  onClick={(e) => moreOtherClick(e, item)}
                  key={index}
                >
                  <div className={'so-filter-condition-item flex items-center px-[6px]'}>
                    <div className={`pr-[3px] ${moreOtherHas?.[item.parameterCode]?.length ? 'text-[#0093E6]' : ''}`}>
                      {item.name}
                    </div>
                    {!moreOtherHas?.[item.parameterCode]?.length ? (
                      popoverKey === item.parameterCode ? (
                        <div
                          className={`icon rotate-180 transform ${popoverKey === item.parameterCode ? 'active' : ''}`}
                        />
                      ) : (
                        <div className={'icon'} />
                      )
                    ) : (
                      <CloseCircleFilled className={'text-[#0093E6]'} onClick={() => moreOtherDeleteList(item)} />
                    )}
                    {popoverKey === item.parameterCode && (
                      <div className={'absolute bottom-[-1px] left-0 z-[12] h-[2px] w-full bg-white'}></div>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
        {popoverKey && (
          <div
            key={popoverKey + '-' + JSON.stringify(props?.sendSearchParams)}
            style={{
              top: top + 'px',
            }}
            className={'absolute left-0 z-[11] rounded-[3px] border border-solid border-[#D9D9D9] bg-white pt-[14px]'}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {popoverKey === '包装'
              ? bzPopoverContent()
              : popoverKey === '标签'
                ? bqPopoverContent()
                : popoverKey === '认证信息'
                  ? rzPopoverContent()
                  : renderMoreOther()}
          </div>
        )}
      </li>
    </ul>
  );
});

Index.displayName = 'Index';

export default memo(Index);
